/**
 * ShareStudioLite — simplified mobile-first sharing surface.
 * Designed for ~10-second share flow after publishing an activity.
 * 
 * No template picker, no layout settings, no platform selection.
 * Just: preview + caption + share/copy buttons.
 */

import { useState, useMemo, useCallback } from 'react';
import { Share2, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { buildCopyReadyPost, formatPriceSek } from '@/lib/socialPostCopy';
import { Button } from '@/components/ui/button';
import {
  FarmlySheet,
  FarmlySheetHeader,
  FarmlySheetBody,
} from '@/components/ui/FarmlySheet';
import { SheetTitle, SheetDescription } from '@/components/ui/sheet';
import type { ActivityShareTarget } from './ShareTarget';
import { generateCaption } from './captions';
import { buildShareUtmUrl } from './shareUtm';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { sendToEditorialDraft } from './editorial/sendToEditorialDraft';

interface ShareStudioLiteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ActivityShareTarget;
}

function formatDateCompact(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return format(new Date(iso), "EEEE d MMMM", { locale: sv });
  } catch {
    return '';
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

export function ShareStudioLite({ open, onOpenChange, target }: ShareStudioLiteProps) {
  const [copied, setCopied] = useState(false);
  const [isSendingToEditorial, setIsSendingToEditorial] = useState(false);
  const [editorialOptInDone, setEditorialOptInDone] = useState(false);
  const coverUrl = useCoverImageUrl('event', target.id, 640);

  const shareUrl = useMemo(
    () => buildShareUtmUrl(target, 'bio', { format: 'ig-post' }),
    [target],
  );

  const caption = useMemo(
    () => generateCaption(target, 'facebook', 'short'),
    [target],
  );

  const fullShareText = useMemo(() => {
    return buildCopyReadyPost({
      title: target.title,
      dateTimeText: formatDateCompact(target.startDate),
      priceText: target.isFree ? 'Gratis' : formatPriceSek(target.priceOre),
      body: caption,
      link: shareUrl,
    });
  }, [target.title, target.startDate, target.isFree, target.priceOre, caption, shareUrl]);

  const dateText = formatDateCompact(target.startDate);
  const heroImage = target.heroImageUrl || coverUrl;

  const handleNativeShare = useCallback(async () => {
    track('share_lite_share_clicked', {
      activity_id: target.id,
      has_native_share: !!navigator.share,
    }, 'share');

    const shareData: ShareData = {
      title: target.title,
      text: caption,
      url: shareUrl,
    };

    try {
      if (typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(shareData))) {
        track('share_lite_native_opened', { activity_id: target.id }, 'share');
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }

    // Fallback: copy full text + URL
    const ok = await copyToClipboard(fullShareText);
    if (ok) {
      track('share_lite_fallback_clipboard', { activity_id: target.id }, 'share');
      toast.success('Text och länk kopierade!', { description: 'Klistra in i din app.' });
    } else {
      track('share_lite_failed', { activity_id: target.id }, 'share');
      toast.error('Kunde inte dela', { description: 'Prova kopiera manuellt.' });
    }
  }, [target, caption, shareUrl, fullShareText]);

  const handleCopyCaption = useCallback(async () => {
    track('share_lite_copy_caption', { activity_id: target.id }, 'share');
    const ok = await copyToClipboard(fullShareText);
    if (ok) {
      setCopied(true);
      toast.success('Inlägget kopierat — klistra in i Facebook eller Instagram.');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Kunde inte kopiera');
    }
  }, [fullShareText, target.id]);

  const handleSendToEditorial = useCallback(async () => {
    if (isSendingToEditorial) return;
    if (target.type !== 'activity') {
      toast.error('Kunde inte spara tillstånd för Farmly-delning');
      return;
    }

    setIsSendingToEditorial(true);
    try {
      const result = await sendToEditorialDraft({
        target,
        caption,
        shareUrl,
        surface: 'lite',
      });

      if (!result.ok) {
        throw new Error(result.code || 'create_social_post_draft_failed');
      }

      setEditorialOptInDone(true);
      toast.success('Tack — Farmly får också dela detta', {
        description: 'Innehållet finns nu i Farmlys redaktionella arbetsflöde.',
      });
    } catch {
      toast.error('Kunde inte spara tillstånd för Farmly-delning');
    } finally {
      setIsSendingToEditorial(false);
    }
  }, [target, caption, shareUrl, isSendingToEditorial]);

  return (
    <FarmlySheet open={open} onOpenChange={onOpenChange}>
      <FarmlySheetHeader>
        <SheetTitle className="text-base font-semibold">Dela aktiviteten</SheetTitle>
        <SheetDescription className="sr-only">
          Dela din publicerade aktivitet via sociala medier eller kopiera texten.
        </SheetDescription>
      </FarmlySheetHeader>

      <FarmlySheetBody className="space-y-5 pb-8">
        {/* Preview card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {/* Hero image */}
          {heroImage ? (
            <div className="aspect-[16/9] bg-muted overflow-hidden">
              <img
                src={heroImage}
                alt={target.title}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="aspect-[16/9] bg-muted flex items-center justify-center">
              <span className="text-4xl opacity-30">🌾</span>
            </div>
          )}

          {/* Info below image */}
          <div className="p-4 space-y-1.5">
            <h3 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">
              {target.title}
            </h3>
            {dateText && (
              <p className="text-xs text-muted-foreground capitalize">{dateText}</p>
            )}
            {target.venueText && (
              <p className="text-xs text-muted-foreground">📍 {target.venueText}</p>
            )}
            {target.producerName && (
              <p className="text-xs text-muted-foreground">Hos {target.producerName}</p>
            )}
          </div>
        </div>

        {/* Caption preview */}
        <div className="rounded-lg bg-muted/50 border border-border p-3">
          <p className="text-xs text-muted-foreground mb-1 font-medium">Text som delas:</p>
          <p className="text-sm text-foreground whitespace-pre-line leading-relaxed">
            {caption}
          </p>
          <p className="text-xs text-primary mt-2 break-all">{shareUrl}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2.5">
          <p className="text-xs text-muted-foreground">
            Det här underlaget är till för att du enkelt ska kunna dela aktiviteten själv.
          </p>
          <Button
            size="lg"
            className="w-full gap-2 text-sm font-medium"
            onClick={handleNativeShare}
            data-testid="share-lite-share-btn"
          >
            <Share2 className="h-4 w-4" />
            Dela själv
          </Button>

          <Button
            variant="outline"
            size="lg"
            className="w-full gap-2 text-sm"
            onClick={handleCopyCaption}
            data-testid="share-lite-copy-btn"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Kopierat!' : 'Kopiera inlägg'}
          </Button>

          <p className="text-xs text-muted-foreground pt-1">
            Om du vill kan Farmly också lyfta den här aktiviteten i våra egna kanaler.
          </p>

          {editorialOptInDone ? (
            <div className="flex items-center gap-1.5 py-1 text-xs text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-primary" />
              Farmly delar också detta
            </div>
          ) : (
            <Button
              variant="secondary"
              size="lg"
              className="w-full gap-2 text-sm"
              onClick={handleSendToEditorial}
              disabled={isSendingToEditorial || target.type !== 'activity'}
              data-testid="share-lite-editorial-btn"
            >
              {isSendingToEditorial ? 'Sparar…' : 'Farmly får också dela detta'}
            </Button>
          )}
        </div>
      </FarmlySheetBody>
    </FarmlySheet>
  );
}
